/*
 * Smores Article — auto-TOC builder and share helpers.
 *
 * Walks the article body and looks for headings with a [data-toc-anchor]
 * attribute, or any <h2> with an id. For each, it appends a link to the
 * sidebar TOC. Active link tracking uses IntersectionObserver — the
 * closest heading above the viewport top becomes "current".
 *
 * Share links use Web Share API when available, with a clipboard
 * fallback. AI summarize chips deep-link to the chat product with the
 * article URL prefilled as a question prompt.
 */
(() => {
  const article = document.querySelector('[data-smores-article]');
  if (!article) return;

  const body = article.querySelector('[data-article-body]');
  const tocList = article.querySelector('[data-article-toc]');

  // Build TOC from H2s inside the rich-text body
  if (body && tocList) {
    const headings = [...body.querySelectorAll('h2')];
    headings.forEach((h, i) => {
      if (!h.id) h.id = h.textContent.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `heading-${i}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent;
      a.dataset.tocLink = h.id;
      li.appendChild(a);
      tocList.appendChild(li);
    });

    // Highlight the current heading as you scroll
    if (headings.length > 0 && 'IntersectionObserver' in window) {
      const links = [...tocList.querySelectorAll('[data-toc-link]')];
      const byId = new Map(links.map((l) => [l.dataset.tocLink, l]));
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const link = byId.get(entry.target.id);
            if (!link) return;
            if (entry.isIntersecting) {
              links.forEach((l) => l.removeAttribute('aria-current'));
              link.setAttribute('aria-current', 'true');
            }
          });
        },
        { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
      );
      headings.forEach((h) => observer.observe(h));
    }
  }

  // Share buttons (Web Share or clipboard fallback)
  article.querySelectorAll('[data-share-action]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const url = btn.dataset.url || window.location.href;
      const title = btn.dataset.title || document.title;
      const platform = btn.dataset.shareAction;

      if (platform === 'native') {
        if (navigator.share) {
          try { await navigator.share({ url, title }); return; } catch (_) { return; }
        }
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url);
          btn.setAttribute('aria-label', 'Link copied');
          return;
        }
      }
      if (platform === 'copy' && navigator.clipboard) {
        navigator.clipboard.writeText(url);
        btn.setAttribute('aria-label', 'Link copied');
        return;
      }

      // Platform deep-links
      const enc = encodeURIComponent;
      const map = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
        twitter:  `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
        pinterest:`https://pinterest.com/pin/create/button/?url=${enc(url)}&description=${enc(title)}`,
        instagram:`https://www.instagram.com/`
      };
      if (map[platform]) {
        window.open(map[platform], '_blank', 'noopener,width=600,height=500');
      }
    });
  });

  // AI summarize chips
  article.querySelectorAll('[data-summarize]').forEach((chip) => {
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      const url = window.location.href;
      const prompt = `Summarize this article in 5 bullet points: ${url}`;
      const enc = encodeURIComponent;
      const map = {
        chatgpt: `https://chatgpt.com/?q=${enc(prompt)}`,
        claude:  `https://claude.ai/new?q=${enc(prompt)}`,
        gemini:  `https://gemini.google.com/app?q=${enc(prompt)}`
      };
      const target = map[chip.dataset.summarize];
      if (target) window.open(target, '_blank', 'noopener');
    });
  });
})();